import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { type FC } from 'react';

import { type RouterNetworkSimSummary } from '@okvc/schema';

import { CARRIER_LABELS } from '../routerSimForm.js';

interface SimSwitchConfirmDialogProps {
  sim: RouterNetworkSimSummary | null;
  switching: boolean;
  switchError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const SimSwitchConfirmDialog: FC<SimSwitchConfirmDialogProps> = ({ sim, switching, switchError, onConfirm, onCancel }) => {
  const carrier = sim ? CARRIER_LABELS[sim.carrier] : '';
  const staysOnSim = sim?.isActive ?? false;

  return (
    <Dialog open={Boolean(sim)} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{staysOnSim ? `Make ${carrier} the primary SIM?` : `Switch to ${carrier}?`}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {staysOnSim ? (
            <Typography variant="body1">
              The router is already on this SIM. It becomes the primary, so the router returns to it after a failover.
              Video is not interrupted.
            </Typography>
          ) : (
            <>
              <Typography variant="body1">
                {`The router moves to ${carrier} and keeps it as the primary SIM. ` +
                  'It falls back to the other SIM only if this one stops working.'}
              </Typography>
              <Alert severity="warning">
                Video from this router pauses for up to a few minutes while the router reconnects.
              </Alert>
            </>
          )}
          <Typography variant="body2" color="text.secondary">
            Port forwards pinned to the current SIM&apos;s address are changed to accept traffic on any SIM.
          </Typography>
          {switchError ? <Alert severity="error">{switchError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <LoadingButton variant="contained" color="warning" loading={switching} onClick={onConfirm}>
          {staysOnSim ? 'Make primary' : 'Switch SIM'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SimSwitchConfirmDialog;
